import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axiosConfig";
import { TailSpin } from "react-loader-spinner";
import { BiExport } from "react-icons/bi";
import AdminTransactionsExportModal from "../../components/admin-export/AdminTransactionsExportModal";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { API_URL, PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import { useAuth } from "../../context/auth-context";
import { DataTable } from "../../components/data-table";
import type { AdminOrderTransaction } from "../../lib/api/adminTransactionsData";
import { useAdminTransactionSummariesQuery } from "../../lib/api/useAdminTransactionSummariesQuery";
import type { OrderSubtypeFilter } from "../../lib/api/useAdminTransactionsDataQuery";
import { useAdminTransactionsDataQuery } from "../../lib/api/useAdminTransactionsDataQuery";
import type { AdminTransactionCurrencyDistributionItem } from "../../lib/api/adminTransactionSummaries";
import { formatAmount } from "../../utils/FormatPerson";
import AdminOrderDetailModal from "./AdminOrderDetailModal";
import {
  buildOrderTableRows,
  createOrderTableColumns,
  type OrderTableRow,
} from "./ordersOrderTable";

const SEGMENT_COLORS = [
  "#0D851D",
  "#BE6B0A",
  "#8B5CF6",
  "#279DF3",
  "#D92B2B",
  "#4E606E",
  "#EAB308",
  "#EC4899",
  "#14B8A6",
  "#F97316",
];

const TOP_CURRENCIES = 6;

type MergedCurrency = {
  currency: string;
  usdtAmount: number;
  count: number;
  amount: number;
  ngnAmount: number;
};

function mergeCurrencyDistribution(
  items: AdminTransactionCurrencyDistributionItem[]
): MergedCurrency[] {
  const map = new Map<string, MergedCurrency>();
  for (const item of items) {
    const key = item.currency.toUpperCase();
    const prev = map.get(key);
    const addUsdt = Number(item.usdtAmount) || 0;
    const addCount = Number(item.count) || 0;
    const addAmount = Number(item.amount) || 0;
    const addNgn = Number(item.ngnAmount) || 0;
    if (prev) {
      prev.usdtAmount += addUsdt;
      prev.count += addCount;
      prev.amount += addAmount;
      prev.ngnAmount += addNgn;
    } else {
      map.set(key, {
        currency: key,
        usdtAmount: addUsdt,
        count: addCount,
        amount: addAmount,
        ngnAmount: addNgn,
      });
    }
  }
  return Array.from(map.values());
}

function buildBarRows(merged: MergedCurrency[]): MergedCurrency[] {
  const sorted = [...merged].sort((a, b) => b.usdtAmount - a.usdtAmount);
  const top = sorted.slice(0, TOP_CURRENCIES);
  const rest = sorted.slice(TOP_CURRENCIES);
  const restUsdt = rest.reduce((s, r) => s + r.usdtAmount, 0);
  if (restUsdt <= 0) return top;
  return [
    ...top,
    {
      currency: "Other",
      usdtAmount: restUsdt,
      count: rest.reduce((s, r) => s + r.count, 0),
      amount: rest.reduce((s, r) => s + r.amount, 0),
      ngnAmount: rest.reduce((s, r) => s + r.ngnAmount, 0),
    },
  ];
}

type SegmentWithDetail = MergedCurrency & {
  pct: number;
  color: string;
  showLabel: boolean;
  /** Share of total portfolio USDT, not just the bar. */
  pctOfTotalUsdt: number;
};

function formatStatusLabel(key: string): string {
  if (key === "COMEPLETED") return "Completed";
  return key
    .toLowerCase()
    .split(/_/g)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusAccent(key: string): { bar: string; glow: string } {
  const u = key.toUpperCase();
  if (u === "COMPLETED" || u === "SUCCESSFUL" || u === "COMEPLETED") {
    return { bar: "#03C668", glow: "rgba(3, 198, 104, 0.12)" };
  }
  if (u === "FAILED") {
    return { bar: "#DF4E4E", glow: "rgba(223, 78, 78, 0.12)" };
  }
  if (u === "PENDING") {
    return { bar: "#F28A0F", glow: "rgba(242, 138, 15, 0.12)" };
  }
  if (u === "HOLD") {
    return { bar: "#869AA9", glow: "rgba(134, 154, 169, 0.12)" };
  }
  return { bar: "#0B75C2", glow: "rgba(11, 117, 194, 0.12)" };
}

const ORDER_TYPE_META: Record<
  string,
  { label: string; bar: string; glow: string }
> = {
  deposit: {
    label: "Deposit",
    bar: "#03C668",
    glow: "rgba(3, 198, 104, 0.12)",
  },
  sell: { label: "Sell", bar: "#F28A0F", glow: "rgba(242, 138, 15, 0.12)" },
  swap: { label: "Swap", bar: "#8B5CF6", glow: "rgba(139, 92, 246, 0.14)" },
  withdrawal: {
    label: "Withdrawal",
    bar: "#279DF3",
    glow: "rgba(39, 157, 243, 0.12)",
  },
};

function orderTypeAccent(key: string): {
  label: string;
  bar: string;
  glow: string;
} {
  const k = key.toLowerCase();
  const meta = ORDER_TYPE_META[k];
  if (meta) return meta;
  return {
    label: formatStatusLabel(key),
    bar: "#4E606E",
    glow: "rgba(78, 96, 110, 0.12)",
  };
}

const ORDER_TABLE_LIMIT = 25;

const ORDER_SUBTYPE_TABS: { id: OrderSubtypeFilter; label: string }[] = [
  { id: "sell", label: "Sell" },
  { id: "deposit", label: "Deposit" },
  { id: "swap", label: "Swap" },
  { id: "withdrawal", label: "Withdrawal" },
];

function orderSubtypeLabel(subtype: OrderSubtypeFilter): string {
  return (
    ORDER_SUBTYPE_TABS.find((tab) => tab.id === subtype)?.label ?? "Orders"
  );
}

const OrdersOverview: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoWithdraw, setAutoWithdraw] = useState<string>("");
  const [autoWithdrawBusy, setAutoWithdrawBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    axiosInstance
      .get(`${API_URL}api/settings`)
      .then((r: { data: { data: { autoWithdrawalStatus?: string } } }) => {
        setAutoWithdraw(r.data.data.autoWithdrawalStatus ?? "");
      })
      .catch((e: { response?: { data?: { message?: string } } }) => {
        console.log(e.response?.data?.message);
      });
  }, [isAuthenticated]);

  const toggleAutoWithdraw = () => {
    if (autoWithdrawBusy || !isAuthenticated) return;
    const activating = autoWithdraw !== "active";
    setAutoWithdrawBusy(true);
    axiosInstance
      .patch(`${API_URL}api/settings/autowithdrawal-status`, {
        status: activating ? "active" : "inactive",
      })
      .then(() => {
        setAutoWithdraw(activating ? "active" : "inactive");
      })
      .catch((e: { response?: { data?: { message?: string } } }) => {
        console.log(e.response?.data?.message);
      })
      .finally(() => setAutoWithdrawBusy(false));
  };

  const [orderSubtype, setOrderSubtype] = useState<OrderSubtypeFilter>("sell");
  const [tablePage, setTablePage] = useState(1);
  const [tableSearch, setTableSearch] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTransaction, setDetailTransaction] =
    useState<AdminOrderTransaction | null>(null);

  const dateRangeInvalid = Boolean(startDate && endDate) && startDate > endDate;

  const queryDates = useMemo(() => {
    if (startDate && endDate && !dateRangeInvalid) {
      return { startDate, endDate };
    }
    return {};
  }, [startDate, endDate, dateRangeInvalid]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminTransactionSummariesQuery({
      ...queryDates,
      orderSubtype,
      enabled: !dateRangeInvalid,
    });

  const barRows = useMemo(() => {
    if (!data?.currencyDistribution?.length) return [];
    const merged = mergeCurrencyDistribution(data.currencyDistribution);
    return buildBarRows(merged);
  }, [data?.currencyDistribution]);

  const segments = useMemo((): SegmentWithDetail[] => {
    if (!data || barRows.length === 0) return [];
    const barTotal = barRows.reduce((sum, row) => sum + row.usdtAmount, 0);
    if (barTotal <= 0) return [];
    const totalUsdt = Number(data.totalOrderUSDT) || barTotal;
    return barRows.map((row, i) => {
      const pct = (row.usdtAmount / barTotal) * 100;
      const pctOfTotalUsdt =
        totalUsdt > 0 ? (row.usdtAmount / totalUsdt) * 100 : 0;
      return {
        ...row,
        pct,
        pctOfTotalUsdt,
        color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
        showLabel: pct >= 5,
      };
    });
  }, [barRows, data]);

  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

  useEffect(() => {
    if (segments.length === 0) {
      setSelectedCurrency(null);
      return;
    }
    setSelectedCurrency((prev) =>
      prev && segments.some((s) => s.currency === prev)
        ? prev
        : segments[0].currency
    );
  }, [segments]);

  const selectedSegment = useMemo(() => {
    if (segments.length === 0) return null;
    return segments.find((s) => s.currency === selectedCurrency) ?? segments[0];
  }, [segments, selectedCurrency]);

  const statusEntries = useMemo(() => {
    if (!data?.statusBreakdown) return [];
    return Object.entries(data.statusBreakdown).sort((a, b) => b[1] - a[1]);
  }, [data?.statusBreakdown]);

  const orderTypeEntries = useMemo(() => {
    const raw = data?.orderTypeBreakdown;
    if (!raw || typeof raw !== "object") return [];
    return Object.entries(raw).sort((a, b) => b[1] - a[1]);
  }, [data?.orderTypeBreakdown]);

  const orderTypeTotal = useMemo(
    () => orderTypeEntries.reduce((s, [, n]) => s + n, 0),
    [orderTypeEntries]
  );

  useEffect(() => {
    setTablePage(1);
  }, [startDate, endDate, orderSubtype, tableSearch, dateRangeInvalid]);

  const {
    data: tablePayload,
    isPending: tablePending,
    isError: tableError,
    error: tableQueryError,
    refetch: refetchTable,
    isFetching: tableFetching,
  } = useAdminTransactionsDataQuery({
    page: tablePage,
    orderSubtype,
    search: tableSearch,
    limit: ORDER_TABLE_LIMIT,
    ...queryDates,
    enabled: !dateRangeInvalid,
  });

  const transactions = useMemo(
    () => tablePayload?.transactions ?? [],
    [tablePayload?.transactions]
  );
  const pagination = tablePayload?.pagination;

  const tableRows = useMemo(
    () => buildOrderTableRows(transactions),
    [transactions]
  );

  const openDetail = useCallback((row: OrderTableRow) => {
    setDetailTransaction(row.raw);
    setDetailOpen(true);
  }, []);

  const orderColumns = useMemo(
    () => createOrderTableColumns(openDetail),
    [openDetail]
  );

  const tableTotalPages = Math.max(1, pagination?.totalPages ?? 1);
  const tableTotalCount = pagination?.totalCount ?? 0;

  const subtypeLabel = orderSubtypeLabel(orderSubtype);
  const subtypeLower = subtypeLabel.toLowerCase();

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="flex-1 px-5 pt-6 pb-24 mds:px-8 lgss:px-10 font-manrope bg-[#13181B] max-w-[1600px] mx-auto w-full">
            <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-8">
              <div>
                <h1 className="font-sora font-semibold text-white text-xl tracking-tight">
                  Orders
                </h1>
                <p className="text-[13px] text-[#869AA9] mt-1 max-w-xl leading-relaxed">
                  Volume and mix for the selected order type and period. Leave
                  dates empty to load all-time totals.
                </p>
              </div>

              <div
                className="rounded-xl border border-[#2C363E] bg-[#161E25]/80 p-4 shadow-[0_4px_24px_rgba(0,0,0,0.25)] w-full lg:w-auto lg:min-w-[320px]"
                role="group"
                aria-label="Date range filter"
              >
                <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider mb-3">
                  Date range
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="orders-summary-start"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      From
                    </label>
                    <input
                      id="orders-summary-start"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label
                      htmlFor="orders-summary-end"
                      className="block text-[11px] text-[#5C6B76] mb-1"
                    >
                      To
                    </label>
                    <input
                      id="orders-summary-end"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full h-10 rounded-lg border border-[#2C363E] bg-[#1C2227] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 transition shadow-inner"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                    className="h-10 shrink-0 px-4 rounded-lg border border-[#2C363E] text-[12px] font-semibold text-[#AEBCC6] hover:text-white hover:bg-[#273037] hover:border-[#3A4852] transition active:scale-[0.98]"
                  >
                    Reset
                  </button>
                </div>
                {(startDate || endDate) && !(startDate && endDate) && (
                  <p className="mt-2 text-[11px] text-[#5C6B76]">
                    Select both dates to filter; otherwise all-time data is
                    shown.
                  </p>
                )}
              </div>
            </header>

            <div
              className="flex flex-wrap gap-2 mb-6"
              role="tablist"
              aria-label="Order subtype filter"
            >
              {ORDER_SUBTYPE_TABS.map((tab) => {
                const active = orderSubtype === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setOrderSubtype(tab.id)}
                    className={`rounded-lg px-3 py-2 text-[12px] font-semibold transition border ${
                      active
                        ? "border-secondary_button bg-[#1C2227] text-secondary_button"
                        : "border-[#2C363E] text-[#869AA9] hover:border-[#3A4852] hover:text-[#C6D0D7]"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {dateRangeInvalid && (
              <div
                className="rounded-xl border border-amber-500/35 bg-amber-500/[0.07] px-5 py-8 text-center mb-6"
                role="alert"
              >
                <p className="text-[14px] font-semibold text-amber-200/95">
                  Invalid date range
                </p>
                <p className="text-[13px] text-[#AEBCC6] mt-2 max-w-md mx-auto leading-relaxed">
                  Choose an end date on or after the start date, or reset the
                  filter to view summaries.
                </p>
              </div>
            )}

            {!dateRangeInvalid && isPending && (
              <div
                className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40"
                aria-busy="true"
                aria-label="Loading summaries"
              >
                <TailSpin color="#0B75C2" height={40} width={40} />
                <p className="text-[13px] text-[#869AA9]">Loading summaries…</p>
              </div>
            )}

            {!dateRangeInvalid && isError && (
              <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>
                  {error?.response?.data &&
                  typeof error.response.data === "object" &&
                  "message" in error.response.data
                    ? String(
                        (error.response.data as { message?: string }).message
                      )
                    : "Could not load summaries."}
                </span>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
                >
                  Retry
                </button>
              </div>
            )}

            {!dateRangeInvalid && !isPending && !isError && data && (
              <>
                {isFetching && !isPending && (
                  <div className="mb-4 flex items-center gap-2 text-[12px] text-[#869AA9]">
                    <span
                      className="inline-block h-3 w-3 rounded-full border-2 border-[#869AA9] border-t-transparent animate-spin"
                      aria-hidden
                    />
                    Refreshing…
                  </div>
                )}

                <section
                  className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6"
                  aria-label="Key metrics"
                >
                  {[
                    {
                      id: "count",
                      label: `Total ${subtypeLower}`,
                      value: data.totalOrderCount.toLocaleString(),
                      hint: `${subtypeLabel} count in range`,
                    },
                    {
                      id: "usdt",
                      label: `${subtypeLabel} (USDT)`,
                      value: `$${formatAmount(data.totalOrderUSDT)}`,
                      hint: "Notional",
                    },
                    {
                      id: "ngn",
                      label: `${subtypeLabel} (NGN)`,
                      value: `₦${formatAmount(data.totalOrderNGN)}`,
                      hint: "Fiat equivalent",
                    },
                  ].map((kpi) => (
                    <div
                      key={kpi.id}
                      className="rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#1A1F24] px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.2)] hover:border-[#3A4852] transition-colors"
                    >
                      <p className="text-[11px] font-semibold text-[#869AA9] uppercase tracking-wider">
                        {kpi.label}
                      </p>
                      <p className="font-sora text-xl sm:text-2xl font-bold text-white mt-2 tabular-nums tracking-tight">
                        {kpi.value}
                      </p>
                      <p className="text-[11px] text-[#5C6B76] mt-1">
                        {kpi.hint}
                      </p>
                    </div>
                  ))}
                </section>

                <section
                  className="rounded-2xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#181D22] p-5 sm:p-7 mb-8 shadow-[0_8px_32px_rgba(0,0,0,0.28)]"
                  aria-labelledby="currency-mix-heading"
                >
                  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
                    <div>
                      <h2
                        id="currency-mix-heading"
                        className="text-[11px] font-bold text-[#869AA9] uppercase tracking-widest"
                      >
                        Total {subtypeLower} (USDT)
                      </h2>
                      <p className="font-sora font-bold text-[#BEE1FB] text-2xl sm:text-4xl mt-2 tabular-nums">
                        ${formatAmount(data.totalOrderUSDT)}
                      </p>
                      <p className="font-sora text-[13px] text-[#869AA9] mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        <span>₦{formatAmount(data.totalOrderNGN)}</span>
                        <span className="text-[#3A4852]" aria-hidden>
                          ·
                        </span>
                        <span>
                          {data.totalOrderCount.toLocaleString()} {subtypeLower}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-[#2C363E]/80 pt-6">
                    <h3 className="text-[12px] font-semibold text-[#E7EBEE] mb-1">
                      Currency mix
                    </h3>
                    <p className="text-[12px] text-[#5C6B76] mb-2">
                      Select a segment or label to view amounts and counts.
                    </p>

                    {segments.length === 0 ? (
                      <p className="text-[13px] text-[#869AA9] py-6 text-center rounded-lg bg-[#141920]/80 border border-dashed border-[#2C363E]">
                        No currency distribution for this range.
                      </p>
                    ) : (
                      <>
                        <div
                          className="flex w-full gap-1 p-1 rounded-xl bg-[#0D1114] min-h-[44px] shadow-inner"
                          role="group"
                          aria-label="Currency mix. Select a segment for details."
                        >
                          {segments.map((s) => {
                            const isSel = selectedCurrency === s.currency;
                            return (
                              <button
                                key={s.currency}
                                type="button"
                                aria-pressed={isSel}
                                aria-label={`${s.currency}, ${s.pct.toFixed(
                                  2
                                )} percent. Show details.`}
                                onClick={() => setSelectedCurrency(s.currency)}
                                className={`group relative flex items-center justify-center min-h-[44px] min-w-0 first:rounded-l-lg last:rounded-r-lg rounded-md py-2 px-0.5 transition-all hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1114] focus-visible:z-10 ${
                                  isSel
                                    ? "ring-2 ring-white/90 ring-inset z-[1] scale-[1.02]"
                                    : ""
                                }`}
                                style={{
                                  flexGrow: Math.max(s.pct, 0.4),
                                  flexBasis: 0,
                                  backgroundColor: s.color,
                                }}
                              >
                                {s.showLabel ? (
                                  <span className="pointer-events-none truncate px-1 text-[11px] font-semibold text-white drop-shadow-md sm:text-[12px]">
                                    {s.pct < 0.1
                                      ? `${s.pct.toFixed(2)}%`
                                      : `${s.pct.toFixed(1)}%`}
                                  </span>
                                ) : (
                                  <span className="sr-only">
                                    {s.currency}: {s.pct.toFixed(2)}%
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 flex w-full gap-1 px-1">
                          {segments.map((s) => {
                            const isSel = selectedCurrency === s.currency;
                            return (
                              <div
                                key={`leg-${s.currency}`}
                                className="min-w-0 flex justify-center"
                                style={{
                                  flexGrow: Math.max(s.pct, 0.4),
                                  flexBasis: 0,
                                }}
                              >
                                <button
                                  type="button"
                                  aria-pressed={isSel}
                                  onClick={() =>
                                    setSelectedCurrency(s.currency)
                                  }
                                  className={`max-w-full truncate rounded-md px-1.5 py-1 text-[10px] sm:text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary_button ${
                                    isSel
                                      ? "text-white bg-[#2C363E]"
                                      : "text-[#869AA9] hover:text-[#C6D0D7]"
                                  }`}
                                >
                                  {s.currency}
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        {selectedSegment && (
                          <div
                            className="mt-6 rounded-xl border border-[#2C363E] bg-[#141920]/95 p-4 sm:p-5 shadow-[inset_0_0_0_1px_rgba(44,54,62,0.5)]"
                            style={{
                              borderLeftWidth: 4,
                              borderLeftColor: selectedSegment.color,
                            }}
                            role="region"
                            aria-live="polite"
                            aria-label={`Details for ${selectedSegment.currency}`}
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-4">
                              <div className="min-w-0">
                                <h4 className="font-sora text-lg font-bold text-white tracking-tight">
                                  {selectedSegment.currency}
                                </h4>
                                {selectedSegment.currency === "Other" ? (
                                  <p className="text-[12px] text-[#869AA9] mt-1 leading-snug">
                                    Combined smaller assets in this chart.
                                    Native amount sums mixed units across coins.
                                  </p>
                                ) : (
                                  <p className="text-[12px] text-[#5C6B76] mt-1">
                                    On-chain / book amount and notionals for
                                    this asset.
                                  </p>
                                )}
                              </div>
                            </div>

                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  Native amount
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#E7EBEE] tabular-nums mt-1 break-all">
                                  {formatAmount(selectedSegment.amount)}{" "}
                                  <span className="text-[#869AA9] font-medium">
                                    {selectedSegment.currency}
                                  </span>
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  USDT notional
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#BEE1FB] tabular-nums mt-1">
                                  ${formatAmount(selectedSegment.usdtAmount)}
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  NGN amount
                                </dt>
                                <dd className="text-[15px] font-semibold text-[#E7EBEE] tabular-nums mt-1">
                                  ₦{formatAmount(selectedSegment.ngnAmount)}
                                </dd>
                              </div>
                              <div className="rounded-lg bg-[#1C2227]/80 border border-[#2C363E]/60 px-3 py-3">
                                <dt className="text-[10px] font-semibold text-[#5C6B76] uppercase tracking-wide">
                                  Order count
                                </dt>
                                <dd className="text-[15px] font-semibold text-white tabular-nums mt-1">
                                  {selectedSegment.count.toLocaleString()}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </section>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                  <section aria-labelledby="status-breakdown-heading">
                    <div className="flex items-baseline justify-between gap-2 mb-4">
                      <h2
                        id="status-breakdown-heading"
                        className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
                      >
                        Status
                      </h2>
                      <span className="text-[11px] text-[#5C6B76]">
                        By transaction state
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {statusEntries.map(([key, count]) => {
                        const { bar, glow } = statusAccent(key);
                        return (
                          <div
                            key={key}
                            className="rounded-xl border border-[#2C363E] bg-[#1C2227] px-4 py-3.5 transition hover:border-[#3A4852] hover:shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
                            style={{
                              boxShadow: `inset 0 0 0 1px ${glow}`,
                              borderLeftWidth: 3,
                              borderLeftColor: bar,
                            }}
                          >
                            <p className="text-[11px] text-[#869AA9] mb-1.5 leading-tight">
                              {formatStatusLabel(key)}
                            </p>
                            <p className="font-sora text-xl font-bold text-white tabular-nums">
                              {count.toLocaleString()}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {orderTypeEntries.length > 0 && (
                    <section aria-labelledby="order-type-heading">
                      <div className="flex items-baseline justify-between gap-2 mb-4">
                        <h2
                          id="order-type-heading"
                          className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest"
                        >
                          Order types
                        </h2>
                        <span className="text-[11px] text-[#5C6B76]">
                          Mix of {orderTypeTotal.toLocaleString()} classified
                        </span>
                      </div>
                      <div className="space-y-3">
                        {orderTypeEntries.map(([key, count]) => {
                          const pct =
                            orderTypeTotal > 0
                              ? (count / orderTypeTotal) * 100
                              : 0;
                          const { label, bar, glow } = orderTypeAccent(key);
                          return (
                            <div
                              key={key}
                              className="rounded-xl border border-[#2C363E] bg-[#1C2227] p-4 transition hover:border-[#3A4852]"
                              style={{
                                boxShadow: `inset 0 0 0 1px ${glow}`,
                              }}
                            >
                              <div className="flex items-center justify-between gap-3 mb-2">
                                <span className="text-[13px] font-semibold text-[#E7EBEE]">
                                  {label}
                                </span>
                                <span className="text-[13px] tabular-nums text-[#AEBCC6]">
                                  <span className="font-sora font-bold text-white">
                                    {count.toLocaleString()}
                                  </span>
                                </span>
                              </div>
                              <div className="h-2 rounded-full bg-[#0D1114] overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500 ease-out"
                                  style={{
                                    width: `${Math.max(pct, 0)}%`,
                                    backgroundColor: bar,
                                    boxShadow: `0 0 12px ${glow}`,
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </div>
              </>
            )}

            {!dateRangeInvalid && (
              <>
                <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mt-4 mb-3">
                  <h4 className="text-white font-sora font-semibold text-[12px] shrink-0">
                    Activity
                  </h4>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      {autoWithdrawBusy ? (
                        <TailSpin color="#0B75C2" height={20} width={20} />
                      ) : (
                        <button
                          type="button"
                          onClick={toggleAutoWithdraw}
                          aria-pressed={autoWithdraw === "active"}
                          aria-label="Toggle auto withdrawal"
                          className={`flex w-8 h-4 shrink-0 rounded-full transition-all duration-500 cursor-pointer p-0 border-0 ${
                            autoWithdraw === "active"
                              ? "bg-secondary_button"
                              : "bg-gray-600"
                          }`}
                        >
                          <span
                            className={`h-4 w-4 rounded-full transition-all duration-500 bg-gray-100 pointer-events-none ${
                              autoWithdraw === "active" ? "ml-4" : ""
                            }`}
                          />
                        </button>
                      )}
                      <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px]">
                        Auto Withdrawal:{" "}
                        <span
                          className={
                            autoWithdraw === "active"
                              ? "text-[#359943]"
                              : undefined
                          }
                        >
                          {autoWithdraw === "active" ? " ON" : " OFF"}
                        </span>
                      </span>
                    </div>
                    <span
                      className="text-[#3A4852] hidden sm:inline"
                      aria-hidden
                    >
                      |
                    </span>
                    <button
                      type="button"
                      onClick={() => setExportOpen(true)}
                      className="flex items-center justify-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                      aria-label="Export summaries and orders"
                    >
                      <BiExport className="text-white text-lg" aria-hidden />
                      <span className="uppercase font-sora font-semibold text-[#869AA9] text-[10px] mt-0.5">
                        Export
                      </span>
                    </button>
                  </div>
                </div>

                <div className="mt-2 mb-4">
                  <h3 className="text-[12px] font-bold text-[#869AA9] uppercase tracking-widest mb-3">
                    {subtypeLabel}
                  </h3>
                  <div className="mb-4">
                    <input
                      type="search"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      placeholder="Search transactions"
                      className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25]/80 px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
                    />
                  </div>

                  {tableFetching && !tablePending && (
                    <p className="text-[12px] text-[#869AA9] mb-2">
                      Updating list…
                    </p>
                  )}

                  {tableError && (
                    <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <span>
                        {tableQueryError?.response?.data &&
                        typeof tableQueryError.response.data === "object" &&
                        "message" in tableQueryError.response.data
                          ? String(
                              (
                                tableQueryError.response.data as {
                                  message?: string;
                                }
                              ).message
                            )
                          : `Could not load ${subtypeLower} orders.`}
                      </span>
                      <button
                        type="button"
                        onClick={() => refetchTable()}
                        className="shrink-0 rounded-lg bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200 border border-red-500/40"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {tablePending && !tableError && (
                    <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                      <TailSpin color="#0B75C2" height={36} width={36} />
                    </div>
                  )}

                  {!tablePending && !tableError && (
                    <DataTable<OrderTableRow>
                      columns={orderColumns}
                      data={tableRows}
                      currentPage={tablePage}
                      setCurrentPage={setTablePage}
                      totalPage={tableTotalPages}
                      count={tableTotalCount}
                      limit={ORDER_TABLE_LIMIT}
                      emptyContent={`No ${subtypeLower} orders for this filter.`}
                      className="rounded-xl border border-[#2C363E] overflow-hidden"
                      fixedBodyHeight="min(560px, 52vh)"
                    />
                  )}
                </div>

                <AdminOrderDetailModal
                  open={detailOpen}
                  onClose={() => {
                    setDetailOpen(false);
                    setDetailTransaction(null);
                  }}
                  transaction={detailTransaction}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <AdminTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Orders"
        exportContext={{ type: "orders", orderSubtype }}
      />
    </div>
  );
};

export default OrdersOverview;
